import { useContext } from 'react';
import dynamic from 'next/dynamic';
const JoyRideNoSSR = dynamic(() => import('react-joyride'), { ssr: false });
import { ACTIONS, EVENTS, TooltipRenderProps } from 'react-joyride';

import Button from '@components/Button/Button';
import Dialog from '@components/Dialog/Dialog';

import { TourContext } from '@context/tour-context';
import { useUser } from '@context/user-context';

import type { TourStep } from '@context/tour-context';
import type { Step } from 'react-joyride';

import styles from './Tour.module.scss';

function extractAndConvertSuccessiveTooltipSteps(steps: TourStep[]): [Step[], number] {
  const successiveTooltipSteps: Step[] = [];
  const stepIndex = steps.findIndex((step) => step.active);
  let normalizedIndex = 0;

  for (const step of steps.slice(0, stepIndex).reverse()) {
    if (step.type === 'dialog') break;
    successiveTooltipSteps.unshift(normalizeTooltipStep(step));
    normalizedIndex++;
  }

  for (const step of steps.slice(stepIndex)) {
    if (step.type === 'dialog') break;

    successiveTooltipSteps.push(normalizeTooltipStep(step));
  }

  return [successiveTooltipSteps, normalizedIndex];
}

function normalizeTooltipStep(step: TourStep): Step {
  if (step.type === 'dialog') throw new Error('Incorrect step type provided to normalizeTooltipStep');

  return {
    content: step.content,
    target: step.target,
    disableBeacon: true,
    disableOverlayClose: true,
    hideCloseButton: step.hideCloseButton ?? true,
    hideFooter: step.hideFooter ?? true,
    placement: 'bottom',
    spotlightClicks: step.allowClicks ?? false,
    styles: {
      options: {
        zIndex: 10000,
      },
    },
    title: step.title ? step.title : undefined,
    hideBackButton: step.hideBackButton ?? true,
  };
}

const TourComponent: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { tourState, tourSteps, dispatch: tourDispatch } = useContext(TourContext);
  const { user } = useUser();

  if (!user || !user.bbOrgId) {
    return null;
  }

  // Extract next step, if it's a dialog show that, if it's a tooltip then use JoyRide
  const currentStep = tourSteps.find(({ active }) => active);

  if (!currentStep || tourState === 'off' || tourState === 'finish') {
    return null;
  } else if (currentStep.type === 'dialog') {
    return (
      <Dialog show={tourState === 'show'} ariaLabel={currentStep.ariaLabel} maxWidth="800px">
        {currentStep.content}
      </Dialog>
    );
  } else {
    const [nextSteps, normalizedStepIndex] = extractAndConvertSuccessiveTooltipSteps(tourSteps);

    return (
      <JoyRideNoSSR
        run={tourState === 'show'}
        callback={(data) => {
          const { action, type } = data;

          const joyrideEvents = [EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND] as string[];

          if (joyrideEvents.includes(type)) {
            if (action === ACTIONS.PREV) {
              tourDispatch({ type: 'GO_TO_PREV_STEP' });
            } else if (action === ACTIONS.CLOSE) {
              tourDispatch({ type: 'COMPLETE_CURRENT_STEP' });
            } else {
              tourDispatch({ type: 'GO_TO_NEXT_STEP' });
            }
          }
        }}
        locale={{ back: 'Back', close: 'Close', last: 'Finish', next: 'Next', skip: 'Skip' }}
        continuous
        stepIndex={normalizedStepIndex}
        showSkipButton={true}
        spotlightClicks
        hideBackButton
        disableOverlayClose
        steps={nextSteps}
        disableScrolling
        tooltipComponent={Tooltip}
        floaterProps={{ disableAnimation: true }}
      />
    );
  }
};

export default TourComponent;

const Tooltip: React.FC<React.PropsWithChildren<TooltipRenderProps>> = ({
  continuous,
  step,
  backProps,
  closeProps,
  primaryProps,
  tooltipProps,
}) => {
  const { tourSteps } = useContext(TourContext);
  const isLastStep = tourSteps[tourSteps.length - 1].active;

  return (
    <div {...tooltipProps} className={styles['tooltip']}>
      {step.title && (
        <div className={styles['tooltip-title']}>
          <h4>{step.title}</h4>
        </div>
      )}
      <div className={styles['tooltip-content']}>{step.content}</div>
      {!step.hideFooter && (
        <div className={styles['tooltip-footer']}>
          {!step.hideBackButton && (
            <Button {...backProps}>
              <span>Back</span>
            </Button>
          )}
          {continuous && step.hideCloseButton && (
            <Button {...primaryProps}>
              <span>Next</span>
            </Button>
          )}
          {!isLastStep && !step.hideCloseButton && (
            <Button {...closeProps}>
              <span>Close</span>
            </Button>
          )}
          {isLastStep && (
            <Button {...closeProps}>
              <span>Finish</span>
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
